import React, { useEffect, useMemo, useState } from "react";

import { useParams } from "react-router-dom";

import { YogGoalSeoMeta } from "./YogGoalSeoMeta";
import useYogGoalAnalytics from "./useYogGoalAnalytics";
import useLocalStorageSet from "./useLocalStorageSet";

import data from "./yog-goal-asanas.json";

import {
  Page,
  PageInner,
  Breadcrumb,
  BreadcrumbLink,
  BreadcrumbCurrent,
  BreadcrumbSep,
  HeroCard,
  HeroTitle,
  HeroDivider,
  HeroDescription,
  HeroStatsRow,
  HeroStat,
  HeroFocusRow,
  HeroVisual,
  HeroVisualGlyph,
  CategoryPillsRow,
  CategoryPill,
  LayoutGrid,
  Sidebar,
  SidebarHeader,
  ClearAllButton,
  FilterGroup,
  FilterGroupTitle,
  FilterOption,
  ApplyFiltersButton,
  MainCol,
  ResultsBar,
  ResultsCount,
  SortSelect,
  VideoList,
  VideoListCard,
  VideoListCardHeader,
  VideoThumb,
  VideoThumbPlay,
  VideoThumbDuration,
  VideoInfo,
  VideoTitleRow,
  VideoTitle,
  VideoActions,
  IconButton,
  CategoryTag,
  VideoDescription,
  BenefitTagsRow,
  BenefitTag,
  VideoExpandedPanel,
  InlinePlayerWrap,
  Tabs,
  TabButton,
  TabPanel,
  BenefitsList,
  ExpandedActions,
  WatchedButton,
  PaginationRow,
  PageNavButton,
  PageButton,
  EmptyState,
  EmptyStateTitle,
  EmptyStateText,
} from "./YogGoalAsanas.styles";

const PAGE_SIZE = 5;

const getYoutubeThumb = (videoId) =>
  `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

/**
 * Parses a "M:SS" / "MM:SS" duration string (as used throughout
 * yog-goal-asanas.json, e.g. "4:30", "01:01") into whole seconds.
 * Returns null for anything blank or not in that shape — callers should
 * skip nulls rather than coerce them to 0.
 */
const parseDurationToSeconds = (duration) => {
  if (!duration) {
    return null;
  }

  const match = /^(\d{1,3}):(\d{2})$/.exec(duration.trim());

  if (!match) {
    return null;
  }

  return Number(match[1]) * 60 + Number(match[2]);
};

const durationBucketOf = (seconds) => {
  if (seconds === null) {
    return null;
  }
  const minutes = seconds / 60;
  if (minutes < 5) return "short";
  if (minutes <= 15) return "medium";
  return "long";
};

/**
 * Splits the "&&"-joined benefits string from the data into individual
 * sentences, and derives a short chip label from each — most benefit
 * sentences follow a "Short label: fuller explanation." shape (e.g.
 * "Reducing stress and anxiety: The humming sound..."), so splitting on
 * the first colon gives a concise label without truncating mid-word.
 * Falls back to a length-capped version of the full sentence when there's
 * no colon to split on.
 */
const parseBenefits = (benefits) => {
  if (!benefits) {
    return [];
  }

  return benefits
    .split("&&")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((sentence) => {
      const colonIndex = sentence.indexOf(":");
      const label =
        colonIndex > 0 && colonIndex < 60
          ? sentence.slice(0, colonIndex).trim()
          : sentence.length > 46
            ? `${sentence.slice(0, 44).trim()}…`
            : sentence;
      return { label, full: sentence };
    });
};

/**
 * Some `description` values in the source data contain a literal "&&"
 * (e.g. "...right nostril is closed.&&It's important to maintain...") —
 * the same separator convention used in `benefits`, apparently leaked
 * into `description` for a subset of entries upstream. This is a
 * display-only cleanup (the source file itself is left untouched): it
 * just keeps a raw data artifact from showing up as literal text in the
 * UI. Worth fixing at the source eventually.
 */
const cleanDescription = (text) =>
  (text || "").replace(/&&/g, " ").replace(/\s{2,}/g, " ").trim();

const DEFAULT_FILTERS = {
  duration: new Set(),
  practiceType: new Set(),
  focusArea: new Set(),
};

const YogGoalAsanas = () => {
  const { goalId } = useParams();

  const { trackPageView, trackVideoPlay, trackVideoSelect } =
    useYogGoalAnalytics();

  /**
   * No goalId in the route (e.g. the base "Find Your Practice" page)
   * isn't a "not found" case — it means show everything. Only an
   * explicit goalId that doesn't match any known goal counts as
   * "not found" below.
   */
  const goal = useMemo(() => {
    if (!goalId) {
      // The same video can appear under more than one goal in the source
      // data, so dedupe by videoId (keeping the first occurrence) rather
      // than flattening as-is — otherwise duplicate videoId values would
      // collide as React keys and as expanded/watched-state lookups.
      const seenVideoIds = new Set();
      const allVideos = data.goals.flatMap((g) => g.videos || []).filter((v) => {
        if (seenVideoIds.has(v.videoId)) {
          return false;
        }
        seenVideoIds.add(v.videoId);
        return true;
      });
      return {
        id: "all",
        title: "All Yoga Practices",
        description:
          "Browse guided yoga practices across every wellness goal.",
        keywords: [...new Set(data.goals.flatMap((g) => g.keywords || []))],
        videos: allVideos,
      };
    }
    return data.goals.find((item) => item.id === goalId) || null;
  }, [goalId]);

  const videos = goal?.videos || [];

  /**
   * Distinct source categories present in this goal's videos, in the
   * order they first appear — drives both the category pill row and the
   * sidebar's Practice Type checkboxes. Computed per-goal rather than
   * hardcoded, since the real taxonomy (Pranayam / Asan / Yoga for
   * Wellness / ...) varies by goal.
   */
  const categories = useMemo(() => {
    const seen = new Set();
    const list = [];
    videos.forEach((v) => {
      if (v.sourceCategory && !seen.has(v.sourceCategory)) {
        seen.add(v.sourceCategory);
        list.push(v.sourceCategory);
      }
    });
    return list;
  }, [videos]);

  /**
   * Distinct tags present in this goal's videos, most-frequent first —
   * drives the sidebar's Focus Area checkboxes. Many videos have no tags
   * at all, so this can be a short list; that's fine, the group just
   * renders fewer options.
   */
  const focusAreaOptions = useMemo(() => {
    const counts = new Map();
    videos.forEach((v) => {
      (v.tags || []).forEach((tag) => {
        counts.set(tag, (counts.get(tag) || 0) + 1);
      });
    });
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([tag]) => tag);
  }, [videos]);

  /**
   * "Total Duration N min" for the hero — summed only over videos with a
   * parseable duration, so a handful of blank durations just don't add
   * to the total rather than being guessed at.
   */
  const totalDurationLabel = useMemo(() => {
    const totalSeconds = videos.reduce((sum, v) => {
      const s = parseDurationToSeconds(v.duration);
      return s !== null ? sum + s : sum;
    }, 0);
    if (totalSeconds === 0) {
      return null;
    }
    return `${Math.round(totalSeconds / 60)} min`;
  }, [videos]);

  /**
   * "Focus: X, Y, Z" for the hero — the goal's own keywords, title-cased,
   * capped to 3. Real data (goal.keywords), not invented.
   */
  const focusChips = useMemo(() => {
    return (goal?.keywords || [])
      .slice(0, 3)
      .map((k) => k.charAt(0).toUpperCase() + k.slice(1));
  }, [goal]);

  // ── Filter / sort / pagination state ─────────────────────────────────
  const [activeCategory, setActiveCategory] = useState("All");
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [pendingFilters, setPendingFilters] = useState(DEFAULT_FILTERS);
  const [sortBy, setSortBy] = useState("relevance");
  const [page, setPage] = useState(1);
  const [expandedVideoId, setExpandedVideoId] = useState(null);
  const [activeTab, setActiveTab] = useState("about");
  const [shouldAutoplay, setShouldAutoplay] = useState(false);

  const [watchedIds, toggleWatched] = useLocalStorageSet("yog:watched");

  // Reset everything when the goal changes.
  useEffect(() => {
    setActiveCategory("All");
    setAppliedFilters(DEFAULT_FILTERS);
    setPendingFilters(DEFAULT_FILTERS);
    setSortBy("relevance");
    setPage(1);
    setExpandedVideoId(videos[0]?.videoId || null);
    setActiveTab("about");
    setShouldAutoplay(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalId]);

  useEffect(() => {
    if (!goal) {
      return;
    }
    trackPageView(goal.title, window.location.href);
  }, [goal, trackPageView]);

  const toggleFilterValue = (group, value) => {
    setPendingFilters((prev) => {
      const next = new Set(prev[group]);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return { ...prev, [group]: next };
    });
  };

  const handleApplyFilters = () => {
    setAppliedFilters(pendingFilters);
    setPage(1);
  };

  const handleClearAll = () => {
    setPendingFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setActiveCategory("All");
    setPage(1);
  };

  const handleCategoryPillClick = (category) => {
    setActiveCategory(category);
    setPage(1);
  };

  // ── Derived: filtered → sorted → paginated video list ────────────────
  const filteredVideos = useMemo(() => {
    return videos.filter((video) => {
      if (activeCategory !== "All" && video.sourceCategory !== activeCategory) {
        return false;
      }

      if (
        appliedFilters.practiceType.size > 0 &&
        !appliedFilters.practiceType.has(video.sourceCategory)
      ) {
        return false;
      }

      if (appliedFilters.duration.size > 0) {
        const bucket = durationBucketOf(parseDurationToSeconds(video.duration));
        if (!bucket || !appliedFilters.duration.has(bucket)) {
          return false;
        }
      }

      if (appliedFilters.focusArea.size > 0) {
        const tags = video.tags || [];
        const matches = tags.some((t) => appliedFilters.focusArea.has(t));
        if (!matches) {
          return false;
        }
      }

      return true;
    });
  }, [videos, activeCategory, appliedFilters]);

  const sortedVideos = useMemo(() => {
    const list = [...filteredVideos];
    switch (sortBy) {
      case "shortest":
        list.sort((a, b) => {
          const sa = parseDurationToSeconds(a.duration) ?? Infinity;
          const sb = parseDurationToSeconds(b.duration) ?? Infinity;
          return sa - sb;
        });
        break;
      case "longest":
        list.sort((a, b) => {
          const sa = parseDurationToSeconds(a.duration) ?? -1;
          const sb = parseDurationToSeconds(b.duration) ?? -1;
          return sb - sa;
        });
        break;
      case "az":
        list.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "relevance":
      default:
        list.sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0));
        break;
    }
    return list;
  }, [filteredVideos, sortBy]);

  const totalPages = Math.max(1, Math.ceil(sortedVideos.length / PAGE_SIZE));
  const pageVideos = sortedVideos.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const handleToggleExpand = (video) => {
    const isOpening = expandedVideoId !== video.videoId;
    setExpandedVideoId(isOpening ? video.videoId : null);
    setActiveTab("about");

    if (isOpening) {
      setShouldAutoplay(true);
      trackVideoSelect(video, goal.title, "list");
    }
  };

  const handleVideoLoad = (video) => {
    if (shouldAutoplay) {
      trackVideoPlay(video, goal.title);
    }
  };

  if (!goal) {
    return (
      <Page>
        <PageInner>
          <EmptyState>
            <EmptyStateTitle>Yoga practice not found</EmptyStateTitle>
            <EmptyStateText>
              We couldn't find the selected yoga goal.
            </EmptyStateText>
          </EmptyState>
        </PageInner>
      </Page>
    );
  }

  return (
    <Page>
      <YogGoalSeoMeta
        goal={goal}
        activeVideo={videos.find((v) => v.videoId === expandedVideoId) || null}
        videos={videos}
      />

      <PageInner>
        <Breadcrumb aria-label="Breadcrumb">
          <BreadcrumbLink href="/">Home</BreadcrumbLink>
          <BreadcrumbSep>›</BreadcrumbSep>
          <BreadcrumbLink href="/practices">Find Your Practice</BreadcrumbLink>
          <BreadcrumbSep>›</BreadcrumbSep>
          <BreadcrumbCurrent>{goal.title}</BreadcrumbCurrent>
        </Breadcrumb>

        <HeroCard>
          <div>
            <HeroTitle>{goal.title}</HeroTitle>

            <HeroDivider aria-hidden="true" />

            <HeroDescription>{goal.description}</HeroDescription>

            <HeroStatsRow>
              <HeroStat>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
                {videos.length} Videos
              </HeroStat>

              {totalDurationLabel && (
                <HeroStat>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 3" />
                  </svg>
                  Total Duration {totalDurationLabel}
                </HeroStat>
              )}
            </HeroStatsRow>

            {focusChips.length > 0 && (
              <HeroFocusRow>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C8 6 6 10 6 13a6 6 0 0 0 12 0c0-3-2-7-6-11z" />
                </svg>
                Focus: {focusChips.join(", ")}
              </HeroFocusRow>
            )}
          </div>

          <HeroVisual aria-hidden="true">
            <HeroVisualGlyph viewBox="0 0 200 150">
              <ellipse cx="100" cy="60" rx="26" ry="42" fill="#3d6b4f" opacity="0.18" />
              <ellipse
                cx="66"
                cy="86"
                rx="26"
                ry="42"
                fill="#3d6b4f"
                opacity="0.14"
                transform="rotate(-55 66 86)"
              />
              <ellipse
                cx="134"
                cy="86"
                rx="26"
                ry="42"
                fill="#3d6b4f"
                opacity="0.14"
                transform="rotate(55 134 86)"
              />
              <circle cx="100" cy="92" r="10" fill="#c9a84c" opacity="0.5" />
            </HeroVisualGlyph>
          </HeroVisual>
        </HeroCard>

        {categories.length > 0 && (
          <CategoryPillsRow>
            <CategoryPill
              type="button"
              $active={activeCategory === "All"}
              onClick={() => handleCategoryPillClick("All")}
            >
              All
            </CategoryPill>
            {categories.map((category) => (
              <CategoryPill
                key={category}
                type="button"
                $active={activeCategory === category}
                onClick={() => handleCategoryPillClick(category)}
              >
                {category}
              </CategoryPill>
            ))}
          </CategoryPillsRow>
        )}

        <LayoutGrid>
          <Sidebar>
            <SidebarHeader>
              Filter
              <ClearAllButton type="button" onClick={handleClearAll}>
                Clear All
              </ClearAllButton>
            </SidebarHeader>

            <FilterGroup>
              <FilterGroupTitle>Duration</FilterGroupTitle>
              {[
                { value: "short", label: "Short (< 5 min)" },
                { value: "medium", label: "Medium (5 - 15 min)" },
                { value: "long", label: "Long (> 15 min)" },
              ].map((opt) => (
                <FilterOption key={opt.value}>
                  <input
                    type="checkbox"
                    checked={pendingFilters.duration.has(opt.value)}
                    onChange={() => toggleFilterValue("duration", opt.value)}
                  />
                  {opt.label}
                </FilterOption>
              ))}
            </FilterGroup>

            {categories.length > 0 && (
              <FilterGroup>
                <FilterGroupTitle>Practice Type</FilterGroupTitle>
                {categories.map((category) => (
                  <FilterOption key={category}>
                    <input
                      type="checkbox"
                      checked={pendingFilters.practiceType.has(category)}
                      onChange={() =>
                        toggleFilterValue("practiceType", category)
                      }
                    />
                    {category}
                  </FilterOption>
                ))}
              </FilterGroup>
            )}

            {focusAreaOptions.length > 0 && (
              <FilterGroup $scroll={focusAreaOptions.length > 6}>
                <FilterGroupTitle>Focus Area</FilterGroupTitle>
                {focusAreaOptions.map((tag) => (
                  <FilterOption key={tag}>
                    <input
                      type="checkbox"
                      checked={pendingFilters.focusArea.has(tag)}
                      onChange={() => toggleFilterValue("focusArea", tag)}
                    />
                    {tag}
                  </FilterOption>
                ))}
              </FilterGroup>
            )}

            <ApplyFiltersButton type="button" onClick={handleApplyFilters}>
              Apply Filters
            </ApplyFiltersButton>
          </Sidebar>

          <MainCol>
            <ResultsBar>
              <ResultsCount>
                {sortedVideos.length}{" "}
                {sortedVideos.length === 1 ? "Video" : "Videos"} Found
              </ResultsCount>

              <SortSelect
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort videos by"
              >
                <option value="relevance">Most Relevant</option>
                <option value="shortest">Shortest First</option>
                <option value="longest">Longest First</option>
                <option value="az">A–Z</option>
              </SortSelect>
            </ResultsBar>

            {pageVideos.length > 0 ? (
              <VideoList>
                {pageVideos.map((video) => {
                  const expanded = expandedVideoId === video.videoId;
                  const watched = watchedIds.has(video.videoId);
                  const benefits = parseBenefits(video.benefits);

                  const playerUrl = expanded
                    ? [
                        `https://www.youtube.com/embed/${video.videoId}`,
                        "?rel=0",
                        "&modestbranding=1",
                        "&playsinline=1",
                        shouldAutoplay ? "&autoplay=1" : "",
                      ].join("")
                    : "";

                  return (
                    <VideoListCard key={video.videoId} $expanded={expanded}>
                      <VideoListCardHeader
                        type="button"
                        onClick={() => handleToggleExpand(video)}
                        aria-expanded={expanded}
                      >
                        <VideoThumb>
                          <img
                            src={getYoutubeThumb(video.videoId)}
                            alt=""
                            loading="lazy"
                          />
                          <VideoThumbPlay aria-hidden="true">▶</VideoThumbPlay>
                          {video.duration && (
                            <VideoThumbDuration>
                              {video.duration}
                            </VideoThumbDuration>
                          )}
                        </VideoThumb>

                        <VideoInfo>
                          <VideoTitleRow>
                            <VideoTitle>{video.name}</VideoTitle>
                            <VideoActions>
                              <IconButton
                                $active={expanded}
                                aria-hidden="true"
                              >
                                {expanded ? "▲" : "▼"}
                              </IconButton>
                            </VideoActions>
                          </VideoTitleRow>

                          {video.sourceCategory && (
                            <CategoryTag>{video.sourceCategory}</CategoryTag>
                          )}

                          <VideoDescription>
                            {cleanDescription(video.description)}
                          </VideoDescription>

                          {benefits.length > 0 && (
                            <BenefitTagsRow>
                              {benefits.slice(0, 3).map((b) => (
                                <BenefitTag key={b.label}>
                                  {b.label}
                                </BenefitTag>
                              ))}
                            </BenefitTagsRow>
                          )}
                        </VideoInfo>
                      </VideoListCardHeader>

                      {expanded && (
                        <VideoExpandedPanel>
                          <InlinePlayerWrap>
                            <iframe
                              key={`${video.videoId}-${shouldAutoplay}`}
                              src={playerUrl}
                              title={video.name}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              allowFullScreen
                              onLoad={() => handleVideoLoad(video)}
                            />
                          </InlinePlayerWrap>

                          <Tabs>
                            <TabButton
                              type="button"
                              $active={activeTab === "about"}
                              onClick={() => setActiveTab("about")}
                            >
                              About
                            </TabButton>
                            {benefits.length > 0 && (
                              <TabButton
                                type="button"
                                $active={activeTab === "benefits"}
                                onClick={() => setActiveTab("benefits")}
                              >
                                Benefits
                              </TabButton>
                            )}
                          </Tabs>

                          <TabPanel>
                            {activeTab === "about" && <p>{cleanDescription(video.description)}</p>}
                            {activeTab === "benefits" && (
                              <BenefitsList>
                                {benefits.map((b) => (
                                  <li key={b.full}>{b.full}</li>
                                ))}
                              </BenefitsList>
                            )}
                          </TabPanel>

                          <ExpandedActions>
                            <WatchedButton
                              type="button"
                              $done={watched}
                              onClick={() => toggleWatched(video.videoId)}
                            >
                              {watched ? "✓ Watched" : "Mark as Watched"}
                            </WatchedButton>
                          </ExpandedActions>
                        </VideoExpandedPanel>
                      )}
                    </VideoListCard>
                  );
                })}
              </VideoList>
            ) : (
              <EmptyState>
                <EmptyStateTitle>No practices match those filters</EmptyStateTitle>
                <EmptyStateText>
                  Try clearing a filter or choosing a different category.
                </EmptyStateText>
              </EmptyState>
            )}

            {totalPages > 1 && (
              <PaginationRow>
                <PageNavButton
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  ‹
                </PageNavButton>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (n) => (
                    <PageButton
                      key={n}
                      type="button"
                      $active={page === n}
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </PageButton>
                  )
                )}

                <PageNavButton
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  aria-label="Next page"
                >
                  ›
                </PageNavButton>
              </PaginationRow>
            )}
          </MainCol>
        </LayoutGrid>
      </PageInner>
    </Page>
  );
};

YogGoalAsanas.displayName = "YogGoalAsanas";

export default YogGoalAsanas;